import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Table, Tag, Select, Input, Space, Button, Modal, message, Card, Tooltip,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import {
  ReloadOutlined, SearchOutlined, EditOutlined, EyeOutlined,
} from '@ant-design/icons';
import { feedbackAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import { useAuth } from '../store/AuthContext';
import { useNavigate } from 'react-router-dom';
import type { Feedback, FeedbackStatus, FeedbackType } from '../types';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const statusColorMap: Record<FeedbackStatus, string> = {
  NEW: 'blue',
  IN_REVIEW: 'orange',
  RESOLVED: 'green',
  REJECTED: 'default',
};

const typeColorMap: Record<FeedbackType, string> = {
  GENERAL: 'cyan',
  TOOL_CORRECTION: 'purple',
  BUG: 'red',
  BUSINESS: 'gold',
  DATA_ERROR: 'magenta',
  NEW_TOOL: 'geekblue',
};

const AdminFeedbackPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterType, setFilterType] = useState<string | undefined>();
  const [keyword, setKeyword] = useState('');

  // 状态更新弹窗
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<Feedback | null>(null);
  const [editStatus, setEditStatus] = useState<string>('');
  const [editNote, setEditNote] = useState('');
  const [updating, setUpdating] = useState(false);

  // 详情弹窗
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailFeedback, setDetailFeedback] = useState<Feedback | null>(null);

  // 权限检测
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: any = { page, pageSize: pagination.pageSize };
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      if (keyword.trim()) params.keyword = keyword.trim();

      const res = await feedbackAPI.getList(params);
      const data = res.data?.data || res.data;
      setFeedbacks(data.feedbacks || []);
      setPagination(prev => ({
        ...prev,
        page: data.pagination?.page || page,
        total: data.pagination?.total || 0,
      }));
    } catch (err) {
      message.error(t('feedback.adminLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, keyword, pagination.pageSize, language]);

  useEffect(() => {
    if (user?.role === 'admin') fetchData(1);
  }, [filterStatus, filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (pag: TablePaginationConfig) => {
    fetchData(pag.current || 1);
  };

  const handleSearch = () => fetchData(1);

  // 打开编辑弹窗
  const openEdit = (record: Feedback) => {
    setEditingFeedback(record);
    setEditStatus(record.status);
    setEditNote(record.admin_note || '');
    setEditModalVisible(true);
  };

  // 提交状态更新
  const handleUpdate = async () => {
    if (!editingFeedback) return;
    setUpdating(true);
    try {
      await feedbackAPI.updateStatus(editingFeedback.id, {
        status: editStatus,
        adminNote: editNote || undefined,
      });
      message.success(t('feedback.adminUpdateSuccess'));
      setEditModalVisible(false);
      fetchData(pagination.page);
    } catch (err) {
      message.error(t('feedback.adminUpdateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  // 打开详情弹窗
  const openDetail = async (record: Feedback) => {
    try {
      const res = await feedbackAPI.getById(record.id);
      const data = res.data?.data || res.data;
      setDetailFeedback(data);
      setDetailModalVisible(true);
    } catch {
      setDetailFeedback(record);
      setDetailModalVisible(true);
    }
  };

  const statusLabel = (s: FeedbackStatus) => {
    const map: Record<FeedbackStatus, string> = {
      NEW: t('feedback.statusNew'),
      IN_REVIEW: t('feedback.statusInReview'),
      RESOLVED: t('feedback.statusResolved'),
      REJECTED: t('feedback.statusRejected'),
    };
    return map[s] || s;
  };

  const typeLabel = (tp: FeedbackType) => {
    const map: Record<FeedbackType, string> = {
      GENERAL: t('feedback.typeGeneral'),
      TOOL_CORRECTION: t('feedback.typeToolCorrection'),
      BUG: t('feedback.typeBug'),
      BUSINESS: t('feedback.typeBusiness'),
      DATA_ERROR: t('feedback.typeDataError'),
      NEW_TOOL: t('feedback.typeNewTool'),
    };
    return map[tp] || tp;
  };

  const columns: ColumnsType<Feedback> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: t('feedback.colTime'),
      dataIndex: 'created_at',
      width: 160,
      render: (val: string) => new Date(val).toLocaleString(language === 'zh-CN' ? 'zh-CN' : 'en-US'),
    },
    {
      title: t('feedback.type'),
      dataIndex: 'type',
      width: 110,
      render: (val: FeedbackType) => <Tag color={typeColorMap[val]}>{typeLabel(val)}</Tag>,
    },
    {
      title: t('feedback.colStatus'),
      dataIndex: 'status',
      width: 100,
      render: (val: FeedbackStatus) => <Tag color={statusColorMap[val]}>{statusLabel(val)}</Tag>,
    },
    {
      title: t('feedback.colTool'),
      dataIndex: 'tool_name_snapshot',
      width: 140,
      ellipsis: true,
      render: (val: string, record: Feedback) => val || (record.tool?.name) || '-',
    },
    {
      title: t('feedback.message'),
      dataIndex: 'message',
      ellipsis: true,
      render: (val: string) => (
        <Tooltip title={val}>
          <Text style={{ maxWidth: 260 }} ellipsis>{val}</Text>
        </Tooltip>
      ),
    },
    {
      title: t('feedback.colContact'),
      dataIndex: 'contact',
      width: 140,
      ellipsis: true,
      render: (val: string) => val || '-',
    },
    {
      title: t('feedback.colActions'),
      width: 120,
      render: (_: any, record: Feedback) => (
        <Space>
          <Tooltip title={t('feedback.viewDetail')}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          </Tooltip>
          <Tooltip title={t('feedback.updateStatus')}>
            <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const statusOptions = [
    { value: 'NEW', label: t('feedback.statusNew') },
    { value: 'IN_REVIEW', label: t('feedback.statusInReview') },
    { value: 'RESOLVED', label: t('feedback.statusResolved') },
    { value: 'REJECTED', label: t('feedback.statusRejected') },
  ];

  const typeOptions = [
    { value: 'GENERAL', label: t('feedback.typeGeneral') },
    { value: 'TOOL_CORRECTION', label: t('feedback.typeToolCorrection') },
    { value: 'BUG', label: t('feedback.typeBug') },
    { value: 'BUSINESS', label: t('feedback.typeBusiness') },
    { value: 'DATA_ERROR', label: t('feedback.typeDataError') },
    { value: 'NEW_TOOL', label: t('feedback.typeNewTool') },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '24px auto', padding: '0 16px' }}>
      <Title level={3}>{t('feedback.adminTitle')}</Title>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            allowClear
            style={{ width: 150 }}
            placeholder={t('feedback.filterStatus')}
            options={statusOptions}
            value={filterStatus}
            onChange={val => setFilterStatus(val)}
          />
          <Select
            allowClear
            style={{ width: 150 }}
            placeholder={t('feedback.filterType')}
            options={typeOptions}
            value={filterType}
            onChange={val => setFilterType(val)}
          />
          <Input
            style={{ width: 200 }}
            placeholder={t('feedback.searchKeyword')}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(1)}>
            {t('common.refresh')}
          </Button>
        </Space>
      </Card>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={feedbacks}
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (total) => t('feedback.totalItems', { total: String(total) }),
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        size="small"
      />

      {/* 状态更新弹窗 */}
      <Modal
        title={t('feedback.updateStatusTitle')}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleUpdate}
        confirmLoading={updating}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        {editingFeedback && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>{t('feedback.messageLabel')}</Text>
              <Paragraph type="secondary" ellipsis={{ rows: 3, expandable: true }}>
                {editingFeedback.message}
              </Paragraph>
            </div>
            <div>
              <Text strong>{t('feedback.changeStatusTo')}</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={editStatus}
                options={statusOptions}
                onChange={val => setEditStatus(val)}
              />
            </div>
            <div>
              <Text strong>{t('feedback.adminNote')}：</Text>
              <TextArea
                style={{ marginTop: 8 }}
                rows={3}
                placeholder={t('feedback.optionalNote')}
                value={editNote}
                onChange={e => setEditNote(e.target.value)}
                maxLength={2000}
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title={t('feedback.detailTitle')}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            {t('common.close')}
          </Button>,
          <Button key="edit" type="primary" onClick={() => {
            setDetailModalVisible(false);
            if (detailFeedback) openEdit(detailFeedback);
          }}>
            {t('feedback.process')}
          </Button>,
        ]}
        width={640}
      >
        {detailFeedback && (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div><Text strong>ID：</Text>{detailFeedback.id}</div>
            <div>
              <Text strong>{t('feedback.typePrefix')}</Text>
              <Tag color={typeColorMap[detailFeedback.type]}>{typeLabel(detailFeedback.type)}</Tag>
            </div>
            <div>
              <Text strong>{t('feedback.statusPrefix')}</Text>
              <Tag color={statusColorMap[detailFeedback.status]}>{statusLabel(detailFeedback.status)}</Tag>
            </div>
            {detailFeedback.tool_name_snapshot && (
              <div><Text strong>{t('feedback.toolPrefix')}</Text>{detailFeedback.tool_name_snapshot}</div>
            )}
            {detailFeedback.availability_cn_suggestion && (
              <div><Text strong>{t('feedback.suggestedAvailability')}</Text>{detailFeedback.availability_cn_suggestion}</div>
            )}
            {detailFeedback.evidence_url && (
              <div><Text strong>{t('feedback.evidencePrefix')}</Text><a href={detailFeedback.evidence_url} target="_blank" rel="noopener noreferrer">{detailFeedback.evidence_url}</a></div>
            )}
            <div>
              <Text strong>{t('feedback.messageLabel')}</Text>
              <Paragraph style={{ marginTop: 4 }}>{detailFeedback.message}</Paragraph>
            </div>
            {detailFeedback.contact && (
              <div><Text strong>{t('feedback.contactPrefix')}</Text>{detailFeedback.contact}</div>
            )}
            {detailFeedback.page_url && (
              <div><Text strong>{t('feedback.sourcePagePrefix')}</Text>{detailFeedback.page_url}</div>
            )}
            {detailFeedback.admin_note && (
              <div><Text strong>{t('feedback.adminNote')}：</Text>{detailFeedback.admin_note}</div>
            )}
            <div>
              <Text strong>{t('feedback.submittedAt')}</Text>
              {new Date(detailFeedback.created_at).toLocaleString(language === 'zh-CN' ? 'zh-CN' : 'en-US')}
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default AdminFeedbackPage;
